import { PromotionGroupType, SelectionPromotion } from '@teko/cart/v2';
import {
  Body,
  Divider,
  Flex,
  Row,
  Shape,
  SubTitle,
  styled,
} from '@teko/ds-desktop';
import {
  DefaultPromotion,
  PromotionOption,
  formatCurrency,
} from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/dist/shared';
import {
  DefaultPromo,
  Label,
  StyledProductPrice,
} from 'components/ProductDetail/styles';
import TierPromotions from 'components/TierPromotions';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { t } from 'i18n';
import { StarRating } from 'modules/product-detail/shared/ProductReview/StarRating';
import React, { useMemo } from 'react';
import { ProductPriceSkeleton } from '../../shared/ProductPriceSkeleton';

const MAX_LINES_BEFORE_ELLIPSIS = 2;

export const ProductPriceAndPromotion: React.FC = () => {
  const {
    product,
    price,
    promotionBlocks,
    setSelectedPromotion,
    promotionLoading,
    minAmountByMonth,
  } = useProductDetailState();
  const { productRatingData } = useProductDetailState();
  const { isBot, epicOmni1428, epicOmni1585, epicOmni1550 } = useTenantConfig();
  const showTierPromotionsBlock = !!epicOmni1428 || epicOmni1585;

  const isPromotionDefaultGroupType = (
    promotions: SelectionPromotion[],
    groupType?: PromotionGroupType,
  ) => {
    return groupType
      ? groupType === PromotionGroupType.GROUP_TYPE_DEFAULT
      : promotions.every((p) => !p.isSelectable);
  };

  const newPromotionBlocks = useMemo(() => {
    if (!showTierPromotionsBlock) return promotionBlocks;
    // create a promotion with groupType of GROUP_TYPE_TIER_PROMOTIONS
    const tierPromotionBlock = {
      title: '',
      groupType: PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS,
      promotions: promotionBlocks.flatMap((p) => p.promotions),
      description: '',
    };

    /**  because cart return promotions with groupType in order of Default --> Selectable --> Exclusive
     * we only need to check if default type available or not
     * if yes --> add tier promotions type after default type
     * if no --> add tier promotions to top of the list
     * we also check if there isn't any promotion in the list we return an empty array instead
     */
    return !!promotionBlocks.length
      ? isPromotionDefaultGroupType(
          promotionBlocks[0].promotions,
          promotionBlocks[0].groupType,
        )
        ? [
            promotionBlocks[0],
            tierPromotionBlock,
            ...promotionBlocks.slice(1, promotionBlocks.length),
          ]
        : [tierPromotionBlock, ...promotionBlocks]
      : [];
  }, [promotionBlocks, showTierPromotionsBlock]);

  const mapTierPromotions = (promotions: SelectionPromotion[]) =>
    promotions.map((promotion) => ({
      cartPromotionId: null,
      promotionId: promotion.promotionId,
      promotionCode: promotion.promotionCode,
      isSelectable: promotion.isSelectable,
      imageUrl: promotion.imageUrl,
      title: promotion.title,
      description: promotion.description,
      endedAt: promotion.endedAt?.toString() || '',
      name: promotion.name || '',
      tierDescriptions: promotion.tierDescriptions,
      highlightedDescriptions: promotion.highlightedDescriptions,
      customerTierDescriptions: promotion.customerTierDescriptions,
    }));

  if (!product) return null;

  return (
    <>
      {promotionLoading && !isBot && product.status?.sellable ? (
        <ProductPriceSkeleton marginBottom="s12" />
      ) : (
        !!price?.finalPrice && (
          <>
            <StyledProductPrice
              size="l"
              discountPercent={price.discountPercent}
              supplierRetailPrice={price.strikeThroughDisplayRowTotal}
              latestPrice={price.displayRowTotal}
            />
            {typeof minAmountByMonth === 'number' && (
              <>
                <StyledBody weight="regular" marginRight="s8">
                  {t('Giá mua trả góp từ')}
                </StyledBody>
                <StyledSubTitle level={1} color="#F89E33">
                  {`${formatCurrency(minAmountByMonth)}/${t('tháng')}`}
                </StyledSubTitle>
              </>
            )}
          </>
        )
      )}
      {epicOmni1550 &&
        !!productRatingData.totalReviews &&
        !!productRatingData.averageRating && (
          <Row align="middle" justify="start">
            <StarRating value={productRatingData.averageRating} starSize={16} />
            <Body weight="bold" margin={['s0', 's4']}>
              {productRatingData.averageRating.toFixed(1)}
            </Body>
            <Body
              weight="regular"
              color="textSecondary"
              onClick={() => scrollSmoothToIdWithOffset('review-section')}
            >{`(${productRatingData.totalReviews} ${t(
              'lượt đánh giá',
            )})`}</Body>
          </Row>
        )}
      <Divider dividerStyle={DividerStyleEnum.dashed} color="divider" />

      {newPromotionBlocks?.map(
        ({ title, promotions, description, groupType }, index) => {
          if (!promotions?.length) {
            return;
          }
          const isDefaultGroup = isPromotionDefaultGroupType(
            promotions,
            groupType,
          );

          return isDefaultGroup ? (
            // If all `title` fields in the default promotions array of default group type are empty strings, we should hide this block
            promotions.every((promotion) => !promotion.title) ? null : (
              <DefaultPromo key={index}>
                <Label>
                  <strong style={{ fontWeight: 500 }}>{title}</strong>
                  {!!description ? ` (${description})` : ''}
                </Label>
                <Flex direction="column">
                  {promotions.map((promotion, index) => {
                    return (
                      <StyledDefaultPromotion
                        className={`att-product-detail-default-promotion-${promotion.promotionId}`}
                        key={promotion.promotionId || index}
                        title={promotion.title}
                        imageUrl={promotion.imageUrl}
                        endAt={
                          promotion.endedAt
                            ? `${t('HSD')}: ${new Date(
                                promotion.endedAt,
                              ).toLocaleDateString('vi')}`
                            : undefined
                        }
                        isSelected={promotion.isSelected}
                        highlightedDescriptions={
                          promotion.highlightedDescriptions
                        }
                      />
                    );
                  })}
                </Flex>
              </DefaultPromo>
            )
          ) : groupType === PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS ? (
            <React.Fragment key={index}>
              {epicOmni1428 && (
                <Shape marginTop="s16">
                  <TierPromotions
                    productPromotions={[
                      {
                        title: '',
                        groupType: undefined,
                        promotions: mapTierPromotions(promotions),
                      },
                    ]}
                    maxLinesBeforeEllipsis={MAX_LINES_BEFORE_ELLIPSIS}
                  />
                </Shape>
              )}

              {epicOmni1585 && (
                <Shape marginTop="s16">
                  <TierPromotions
                    productPromotions={[
                      {
                        title: '',
                        groupType: undefined,
                        promotions: mapTierPromotions(promotions),
                      },
                    ]}
                    isCustomerTierPromotions
                  />
                </Shape>
              )}
            </React.Fragment>
          ) : (
            <SelectionPromotionWrapper key={index}>
              <Label>
                <strong style={{ fontWeight: 500 }}>{title}</strong>
                {`${description ? ` (${description})` : ''}`}
              </Label>
              {promotions.map((promotion, index: number) => {
                return (
                  <PromotionOption
                    key={promotion.promotionId}
                    {...getTrackingData({
                      isTrackContent: true,
                      regionName: 'optionalPromotion',
                      contentName: promotion.name,
                      target: 'productDetail',
                      payload: JSON.stringify({
                        sku: product.sku,
                        action: promotion.isSelected ? 'unselect' : 'select',
                        promotionID: promotion.promotionId,
                        screenName: 'productDetail',
                        index,
                      }),
                    })}
                    promotion={promotion}
                    onClick={() => {
                      promotion.isSelected
                        ? setSelectedPromotion?.(undefined)
                        : setSelectedPromotion?.(promotion);
                    }}
                    isSelected={promotion.isSelected && !promotionLoading}
                    isSelectable={promotion.isSelectable && !promotionLoading}
                    className={`att-product-detail-selection-promotion-${promotion.promotionId}`}
                  />
                );
              })}
            </SelectionPromotionWrapper>
          );
        },
      )}
    </>
  );
};

const SelectionPromotionWrapper = styled(Shape)(
  ({ theme }) => `
  margin-top:${theme.spacing.s16};
padding: ${theme.spacing.s16};
background-color: ${theme.colors.background};
border-radius: ${theme.borderRadius.m};
border: ${theme.borderWeight.l} ${theme.colors.border} solid`,
);

const StyledDefaultPromotion = styled(DefaultPromotion)`
  margin: 0.25rem 0;
  line-height: 23px;
  max-width: 90%;
`;

const StyledSubTitle = styled(SubTitle)`
  display: inline;
`;

const StyledBody = styled(Body)`
  display: inline;
`;
